import { useState, useEffect, useCallback, useRef } from 'react';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

const useVersionCheck = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const initialVersionRef = useRef(null);

    const fetchVersion = useCallback(async () => {
        try {
            const response = await fetch('/version.json', {
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache'
                }
            });
            if (!response.ok) {
                return null;
            }
            const data = await response.json();
            return data.version;
        } catch (error) {
            console.warn('Failed to fetch version:', error);
            return null;
        }
    }, []);

    const checkForUpdate = useCallback(async () => {
        if (dismissed) return;

        const currentVersion = await fetchVersion();
        if (!currentVersion) return;

        if (initialVersionRef.current === null) {
            initialVersionRef.current = currentVersion;
            return;
        }

        if (currentVersion !== initialVersionRef.current) {
            setUpdateAvailable(true);
        }
    }, [fetchVersion, dismissed]);

    const refreshApp = useCallback(() => {
        window.location.reload();
    }, []);

    const dismissUpdate = useCallback(() => {
        setDismissed(true);
        setUpdateAvailable(false);
    }, []);

    // Initial check on mount
    useEffect(() => {
        checkForUpdate();
    }, [checkForUpdate]);

    // Poll periodically
    useEffect(() => {
        const interval = setInterval(checkForUpdate, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [checkForUpdate]);

    // Check on tab visibility change
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkForUpdate();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkForUpdate]);

    return {
        updateAvailable: updateAvailable && !dismissed,
        refreshApp,
        dismissUpdate
    };
};

export default useVersionCheck;
